import type { AttendanceOpPackageId } from '@/mocks/db/attendanceOpPackagesCatalog';

/** 台帳の既定ページサイズ。URL の不正値からのフォールバック先も兼ねる。 */
export const DEFAULT_LEDGER_PAGE_SIZE = 50;

/** 業務の系統。フィルタタブ・絞り込み・URL スキーマはここを唯一のソースにする */
export const OPERATION_KINDS = ['event', 'package'] as const;

/**
 * フィルタタブの値。`all` は業務の系統で絞らない。
 *
 * `OPERATION_KINDS` から導出する。リテラルを 3 箇所へ書くと系統を増やしたときに
 * URL スキーマだけ狭いまま残り、`.catch('all')` が実行時も握り潰すため
 * 「タブを押しても絞られない」という気づきにくい壊れ方をする。
 */
export const OPERATION_KIND_FILTERS = ['all', ...OPERATION_KINDS] as const;
export type LedgerOperationKindFilter = (typeof OPERATION_KIND_FILTERS)[number];

/** 業務イベントインスタンス。詳細は `/attendance-core/operation-events/$eventId` */
export interface LedgerOperationEventRef {
  kind: 'event';
  /** attendanceGantt seed の `eventId`。そのままパスパラメータに渡せる */
  id: string;
  /** 同 seed の `titleKey`。表示名は attendance-core の `eventTitleSelectors` から引く */
  titleKey: string;
}

/** 業務パッケージインスタンス。インスタンス詳細ルートは無く、index + search で開く */
export interface LedgerOperationPackageRef {
  kind: 'package';
  /** `/attendance-core/operation-packages` の `operationPackageId` */
  id: AttendanceOpPackageId;
  /** 同 `operationPackagePatternId` */
  patternId: string;
}

/**
 * 台帳 1 行の「業務」列。どの業務インスタンスが回した勤怠締めかを表し、
 * 詳細画面へ遷移するのに必要な最小情報だけを持つ。
 *
 * 表示名はここに持たない。業務名の訳は `pages.attendanceCore` 配下にあるため、
 * `-lib/i18nSelectors.ts` の selector で引く（データ層にロケール文字列を置かない）。
 */
export type LedgerOperationRef = LedgerOperationEventRef | LedgerOperationPackageRef;

/**
 * `leaveRequest` 以外のパッケージは default パターン 1 件だけを持つ
 * （`mocks/db/seeds/attendanceOpPackages.ts` の `defaultPatternId`）。
 * 台帳から辿るのは締め・36協定なので、この規約に乗る。
 */
function packageRef(id: AttendanceOpPackageId): LedgerOperationPackageRef {
  return {
    id,
    kind: 'package',
    patternId: `${id}-pat-default`
  };
}
function eventRef(id: string, titleKey: string): LedgerOperationEventRef {
  return {
    id,
    kind: 'event',
    titleKey
  };
}

/**
 * 勤怠締め run → その run を回した業務。
 *
 * `AttendanceRecord` は業務イベント／業務パッケージへの参照を持たず、attendanceGantt・
 * attendanceOpPackages のモックも勤怠実績を参照しないため、両者を結ぶキーがデータ上に
 * 存在しない。バックエンド接続時は締め run 側が業務インスタンス ID を保持するように
 * なるため、この定数は不要になる。
 *
 * キーは台帳の行 ID（`{対象月}:{勤務体系 ID}`）。同じ月でも勤務体系ごとに締めを回すため、
 * 月だけでは行が衝突する。勤務体系名ではなく ID を使うのは、名前が表示都合で変わっても
 * 対応づけが切れないようにするため。
 *
 * イベント側の `eventId` / `titleKey` は attendanceGantt seed の行と同じ組
 * （ae1〜ae5。seed に無い組を書くと業務名が引けず ID がそのまま出る）。
 */
export const OPERATION_BY_LEDGER_ROW_ID: Record<string, LedgerOperationRef> = {
  // 正社員（フレックス）の月次締め。ae1 → ae2 と月をまたいで続く
  '2026-04:ws-flex': eventRef('ae1', 'monthlyClosingOctober'),
  '2026-05:ws-flex': eventRef('ae2', 'monthlyClosingNovember'),
  // パートタイムは締めサイクルが正社員と別に立つ
  '2026-05:ws-part-hourly': eventRef('ae3', 'partTimeMonthlyClosingMay'),
  // 店舗シフトは締めの前段にシフト編成が入る
  '2026-06:ws-retail-shift': eventRef('ae4', 'shiftOrganizationJun'),
  '2026-06:ws-flex': eventRef('ae5', 'flexWorkPolicyReview'),
  // 36協定の月次モニタリングは締めとは別サイクルで回る（時間外の多い 2 体系）
  '2026-07:ws-flex': packageRef('article36Report'),
  '2026-07:ws-two-shift': packageRef('article36Report')
};

/**
 * 対応づけを持たない run のフォールバック。業務列は空にできない（フィルタタブが
 * 業務イベント / 業務パッケージの 2 値で台帳を分割するため）ので、attendance-core の
 * 締め業務である勤怠締めに寄せる。
 */
export const FALLBACK_OPERATION: LedgerOperationRef = packageRef('attendanceClosing');

/**
 * 締めステータスの進行度。並び順（進行中を上に出す）で比較するため数値で持つ。
 */
export const LEDGER_STATUS_RANK = {
  /** 打刻収集中。対象月がまだ実績の最終月 */
  collecting: 0,
  /** 承認・確認中。月は明けたが締め処理が終わっていない */
  reviewing: 1,
  /** 締め済み。給与へ引き渡せる */
  closed: 2
} as const;
export type LedgerStatus = keyof typeof LEDGER_STATUS_RANK;

/**
 * 締め処理を実行する日（対象月の翌月何日か）。
 *
 * attendanceGantt seed の月次締めは「翌月 1〜2 日に承認・催促 → 翌月 2〜3 日に
 * システム上の勤怠確定」なので、確定日はその後ろに置く。デモデータに締め処理の
 * 実行記録が無いため確定日を実績から復元できず、この規約で組み立てる。
 */
export const CLOSING_DAY_OF_NEXT_MONTH = 3;